import type { NuxtApp } from 'nuxt/app'

/** Internal marker added by transform so getCachedData can check TTL. */
type WithCacheMarker = { __fetchedAt: string }

/**
 * useApiCache – drop-in cache helper for useFetch / useAsyncData.
 *
 * Nuxt automatically passes `key` and `nuxtApp` into getCachedData, so there
 * is nothing extra to provide — just pass the TTL when you want caching.
 *
 *   // useFetch (Nuxt derives the key from URL + query automatically):
 *   const { data } = useFetch('/wallet/transactions', {
 *     query,
 *     ...useApiCache(300),
 *   })
 *
 *   // useAsyncData (you own the key — make it unique per id/filter):
 *   const { data } = useAsyncData(
 *     `transaction-${transactionId.value}`,
 *     () => GetOneTransaction(transactionId.value),
 *     useApiCache(120),
 *   )
 *
 * @param expireTime  Cache TTL in seconds (default: 60)
 * @default 60 seconds
 */
export const useApiCache = <R = Record<string, unknown>>(expireTime = 60) => ({
	/**
	 * Only plain objects are safe to spread — arrays become `{ 0: …, __fetchedAt }` and
	 * primitives are lost entirely if spread into an object literal.
	 * Non-object responses (arrays, primitives, null, undefined) pass through untouched;
	 * getCachedData will find no marker and return undefined, forcing a fresh fetch.
	 */
	transform(input: R | null | undefined) {
		if (!input || typeof input !== 'object' || Array.isArray(input)) return input
		return { ...input, __fetchedAt: new Date().toISOString() } as unknown as R
	},

	/**
	 * Nuxt injects `key` and `nuxtApp` automatically.
	 * Returns `undefined` (not null) to tell Nuxt "no cache – please fetch".
	 * Returns the cached data when still within TTL.
	 */
	getCachedData(key: string, nuxtApp: NuxtApp) {
		const data = (nuxtApp.static.data[key] ?? nuxtApp.payload.data[key]) as (WithCacheMarker & R) | undefined

		// No entry or error response (no marker) → fetch fresh
		if (!data?.__fetchedAt) return undefined

		const cachedAt = new Date(data.__fetchedAt)
		if (isNaN(cachedAt.getTime())) return undefined

		const isExpired = cachedAt.getTime() + expireTime * 1000 < Date.now()
		return isExpired ? undefined : (data as R)
	}
})
